/**
 * EnemyAI — pure behaviour tree for enemy fighters.
 * No Phaser imports — fully testable in Node.js.
 *
 * Behaviour tree: scan → approach → windup → attack → reposition
 * Behaviour variants (MY-1798): grabber / jumper / bomber / shielded / sniper
 * Behaviour variants (MY-1800): biker (strafe + knock-off)
 */

import { tickBikerAI, type BikerAIState, type BikerPhase } from '@/systems/BikerAI';
import { shouldRetreat, RETREAT_SAFE_DIST } from '@/systems/GroupTactics';

export type BehaviourNode =
  | 'scan'
  | 'approach'
  | 'windup'
  | 'attacking'
  | 'reposition'
  | 'grab_windup'
  | 'jump_charge'
  | 'bomb_charge'
  | 'sniper_hold'
  | 'biker_approach'
  | 'biker_strafe'
  | 'biker_recover'
  | 'biker_downed'
  | 'retreating';

/** Pixels — enemies start closing in from this distance. */
export const ENGAGE_RANGE = 150;
/** Pixels — attack trigger distance. */
export const MELEE_RANGE = 70;
/** Frames of telegraph before attack registers. */
const WINDUP_FRAMES = 10;
/** Frames to back off after an attack. */
const REPOSITION_FRAMES = 35;
/** Base frames between attacks at aggressionScale = 0 (easy). */
const BASE_COOLDOWN = 55;
/** Orbit spread radius around the target (px, world-space). */
const ORBIT_RADIUS = 85;
/** 2.5-D Y squash for orbit spread. */
const ORBIT_Y_SQUASH = 0.45;

// — Variant-specific constants —
/** Grabber: frames in grab windup (longer than normal). */
export const GRAB_WINDUP_FRAMES = 16;
/** Grabber: cooldown multiplier after a grab (grabs are slow to repeat). */
export const GRAB_COOLDOWN_MUL = 1.8;
/** Jumper: distance threshold that triggers a jump charge. */
export const JUMP_TRIGGER_RANGE = 120;
/** Jumper: speed multiplier during jump charge. */
export const JUMP_SPEED_MUL = 2.5;
/** Bomber: speed multiplier during kamikaze rush. */
export const BOMB_SPEED_MUL = 3.0;
/** Sniper: preferred minimum distance from player. */
export const SNIPER_RANGE = 200;
/** Sniper: retreat threshold — backs away if player closes inside this. */
export const SNIPER_HOLD_RANGE = 160;

/** Behaviour variant flags — at most one should be set per enemy. */
export interface EnemyBehaviourFlags {
  /** Closes to grab player; player mashes to escape. */
  grabber?: boolean;
  /** Leaps to close distance or over player. */
  jumper?: boolean;
  /** Kamikaze rush then self-destruct AoE. */
  bomber?: boolean;
  /** Blocks frontal hits; must be flanked or grabbed. */
  shielded?: boolean;
  /** Keeps distance; fires ranged attacks. */
  sniper?: boolean;
  /** Strafing motorbike enemy; knock-off spawns a grounded rider + bike hazard. */
  biker?: boolean;
  /** Gang tactics (MY-1887): surround spread when ≥3 attackers; lone low-HP mob retreats. */
  groupTactics?: boolean;
  /**
   * Standard mob can grab + throw the player (MY-1961). Uses grab_windup telegraph.
   * Falls back to normal punch when the player is airborne or invulnerable.
   * Player mashes to escape via GrabEscapeGame.
   */
  canGrab?: boolean;
}

export interface EnemyAIState {
  node: BehaviourNode;
  /** Frames remaining before next attack windup is allowed. */
  cooldown: number;
  /** Frames remaining in the reposition phase. */
  reposition: number;
  /** Frames remaining in the attack windup. */
  windup: number;
  // biker-specific persisted state (MY-1800)
  bikerStrafeDir?: 1 | -1;
  bikerMountedHits?: number;
  bikerKnockedOff?: boolean;
  bikerRecoverFrames?: number;
}

export function initialAIState(): EnemyAIState {
  return { node: 'scan', cooldown: 0, reposition: 0, windup: 0 };
}

export interface EnemyAIInput {
  selfX: number;
  selfY: number;
  targetX: number;
  targetY: number;
  isHitstunned: boolean;
  isKO: boolean;
  isBeingThrown: boolean;
  /** Granted by EnemyGroupManager — enemy will not begin windup if false. */
  canAttack: boolean;
  /** 0 = easy, 1 = hard. Scales cooldown duration and approach speed. */
  aggressionScale: number;
  /** World-space X offset toward the assigned orbit slot (set by GroupManager). */
  orbitOffsetX: number;
  /** World-space Y offset toward the assigned orbit slot (set by GroupManager). */
  orbitOffsetY: number;
  /** Optional behaviour variant flags. */
  behaviourFlags?: EnemyBehaviourFlags;
  /** Player is attacking from behind this enemy — disables shielded blocking. */
  isFlanked?: boolean;
  /** Total live enemies in the group — used for retreat check (MY-1887). */
  attackerCount?: number;
  /** Current HP — used for retreat check (MY-1887). */
  currentHp?: number;
  /** Max HP — used for retreat check (MY-1887). */
  maxHp?: number;
  /** Biker: new hit registered on the mounted rider this frame. */
  bikerHitRegistered?: boolean;
  /** Biker: world width for strafe edge detection (default: 960). */
  worldWidth?: number;
  /** Biker: hit count to knock rider off (overrides BikerAI.KNOCK_OFF_HITS). */
  bikerKnockOffHits?: number;
  /** canGrab mobs: whether the target is currently grounded (default: true). */
  targetIsGrounded?: boolean;
  /** canGrab mobs: whether the target is currently invulnerable (default: false). */
  targetIsInvulnerable?: boolean;
}

export interface EnemyAIOutput {
  /** Normalised movement vector (0,0 when stationary). Magnitude ≤ 1.3. */
  moveX: number;
  moveY: number;
  /** Horizontal facing direction. */
  facing: 1 | -1;
  /** True on the single frame the attack hitbox should be spawned. */
  wantsAttack: boolean;
  nextState: EnemyAIState;
  /** Grabber: true on the frame the grab should be initiated. */
  wantsGrab?: boolean;
  /** Jumper: true on the first frame of the jump charge. */
  wantsJump?: boolean;
  /** Bomber: true on the frame the self-destruct AoE should trigger. */
  wantsBomb?: boolean;
  /** Shielded: true when blocking frontal hits (not flanked). */
  isShieldBlocking?: boolean;
  /** Sniper: true on the frame a ranged attack should be fired. */
  wantsRanged?: boolean;
  /** Biker: contact damage zone active during a strafe pass. */
  wantsStrafeContact?: boolean;
  /** Biker: single-frame knock-off signal — spawn grounded rider + bike hazard. */
  wantsKnockOff?: boolean;
  /** Biker: true in all frames after knock-off. */
  isKnockedOff?: boolean;
}

/**
 * Advance the behaviour tree by one frame.
 * Pure function — returns new state + output. The Phaser adapter (Enemy.ts)
 * applies movement and spawns hitboxes based on the output.
 */
export function tickEnemyAI(input: EnemyAIInput, state: EnemyAIState): EnemyAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return idle(facing, { ...state, node: 'scan' });
  }

  const cooldown = Math.max(0, state.cooldown - 1);
  const reposition = Math.max(0, state.reposition - 1);
  const windup = Math.max(0, state.windup - 1);
  const cooldownDuration = cooldownFor(input.aggressionScale);

  if (input.isHitstunned) {
    return idle(facing, { node: 'scan', cooldown, reposition: 0, windup: 0 });
  }

  const flags = input.behaviourFlags ?? {};

  if (flags.groupTactics) {
    return tickGroupTacticsVariant(
      input,
      state,
      facing,
      cooldown,
      reposition,
      windup,
      cooldownDuration,
    );
  }
  if (flags.grabber) {
    return tickGrabberVariant(input, state, facing, cooldown, reposition, windup, cooldownDuration);
  }
  if (flags.canGrab) {
    return tickCanGrabVariant(input, state, facing, cooldown, reposition, windup, cooldownDuration);
  }
  if (flags.jumper) {
    return tickJumperVariant(input, state, facing, cooldown, reposition, windup, cooldownDuration);
  }
  if (flags.bomber) {
    return tickBomberVariant(input, state, facing, cooldown, reposition, windup, cooldownDuration);
  }
  if (flags.sniper) {
    return tickSniperVariant(input, state, facing, cooldown, reposition, windup, cooldownDuration);
  }
  if (flags.biker) {
    return tickBikerVariant(input, state, facing);
  }

  const out = tickBaseAI(input, state, facing, cooldown, reposition, windup, cooldownDuration);
  if (flags.shielded) {
    return { ...out, isShieldBlocking: !input.isFlanked };
  }
  return out;
}

/** Compute the orbit X/Y offset for enemy at `index` within a group of `total`. */
export function computeOrbitOffset(index: number, total: number): { x: number; y: number } {
  const angle = (2 * Math.PI * index) / Math.max(total, 1);
  return {
    x: Math.cos(angle) * ORBIT_RADIUS,
    y: Math.sin(angle) * ORBIT_RADIUS * ORBIT_Y_SQUASH,
  };
}

// ─── Base AI ────────────────────────────────────────────────────────────────

function tickBaseAI(
  input: EnemyAIInput,
  state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  // — REPOSITION: back off from target after attacking —
  if (reposition > 0) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'reposition', cooldown, reposition, windup: 0 },
    };
  }

  // — WINDUP: stand still while telegraphing the hit —
  if (windup > 0) {
    return idle(facing, { node: state.node, cooldown, reposition: 0, windup });
  }

  // — ATTACK: windup just expired —
  if (state.node === 'windup') {
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      nextState: {
        node: 'reposition',
        cooldown: cooldownDuration,
        reposition: REPOSITION_FRAMES,
        windup: 0,
      },
    };
  }

  const { dist } = toTarget(input);

  // In melee range + allowed + ready → begin windup
  if (dist <= MELEE_RANGE && input.canAttack && cooldown === 0) {
    return idle(facing, { node: 'windup', cooldown, reposition: 0, windup: WINDUP_FRAMES });
  }

  return approachOrbit(input, state, facing, cooldown);
}

// ─── Group tactics variant (MY-1887) ────────────────────────────────────────

function tickGroupTacticsVariant(
  input: EnemyAIInput,
  state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  const wantRetreat = shouldRetreat({
    currentHp: input.currentHp ?? 1,
    maxHp: input.maxHp ?? 1,
    attackerCount: input.attackerCount ?? 1,
  });

  // Already in retreat — keep backing off until safe distance or HP recovered
  if (state.node === 'retreating') {
    if (!wantRetreat) {
      return idle(facing, { node: 'scan', cooldown, reposition: 0, windup: 0 });
    }
    const { dx, dy, dist } = toTarget(input);
    if (dist >= RETREAT_SAFE_DIST) {
      return idle(facing, { node: 'scan', cooldown, reposition: 0, windup: 0 });
    }
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'retreating', cooldown, reposition: 0, windup: 0 },
    };
  }

  // Trigger retreat: lone low-HP mob backs off
  if (wantRetreat) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'retreating', cooldown, reposition: 0, windup: 0 },
    };
  }

  // Surround positioning: orbit offsets already computed by caller (Enemy.ts) using
  // computeSurroundSlot when attackerCount >= SURROUND_MIN_ATTACKERS. Standard base AI.
  return tickBaseAI(input, state, facing, cooldown, reposition, windup, cooldownDuration);
}

// ─── Grabber variant ─────────────────────────────────────────────────────────

function tickGrabberVariant(
  input: EnemyAIInput,
  state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  if (reposition > 0) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'reposition', cooldown, reposition, windup: 0 },
    };
  }

  // Grab windup: stand still
  if (windup > 0) {
    return idle(facing, { node: state.node, cooldown, reposition: 0, windup });
  }

  // Grab fires when grab_windup expires
  if (state.node === 'grab_windup') {
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      wantsGrab: true,
      nextState: {
        node: 'reposition',
        cooldown: Math.round(cooldownDuration * GRAB_COOLDOWN_MUL),
        reposition: REPOSITION_FRAMES,
        windup: 0,
      },
    };
  }

  const { dist } = toTarget(input);

  // Enter grab_windup when in melee range
  if (dist <= MELEE_RANGE && input.canAttack && cooldown === 0) {
    return idle(facing, {
      node: 'grab_windup',
      cooldown,
      reposition: 0,
      windup: GRAB_WINDUP_FRAMES,
    });
  }

  return approachOrbit(input, state, facing, cooldown);
}

// ─── canGrab variant (MY-1961) ───────────────────────────────────────────────

/**
 * Standard mob that can grab + throw the player.
 *
 * When in melee range and the player is grounded + non-invulnerable, the mob
 * enters grab_windup (telegraphed, reactable). Once the windup expires it fires
 * wantsGrab so the Phaser adapter can initiate the hold + GrabEscapeGame.
 *
 * If the player is airborne or invulnerable when the distance check fires, the
 * mob falls back to a normal punch windup instead.
 */
function tickCanGrabVariant(
  input: EnemyAIInput,
  state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  if (reposition > 0) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'reposition', cooldown, reposition, windup: 0 },
    };
  }

  if (windup > 0) {
    return idle(facing, { node: state.node, cooldown, reposition: 0, windup });
  }

  // Grab fires when grab_windup expires
  if (state.node === 'grab_windup') {
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      wantsGrab: true,
      nextState: {
        node: 'reposition',
        cooldown: Math.round(cooldownDuration * GRAB_COOLDOWN_MUL),
        reposition: REPOSITION_FRAMES,
        windup: 0,
      },
    };
  }

  // Normal punch fires when standard windup expires (fallback path)
  if (state.node === 'windup') {
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      nextState: {
        node: 'reposition',
        cooldown: cooldownDuration,
        reposition: REPOSITION_FRAMES,
        windup: 0,
      },
    };
  }

  const { dist } = toTarget(input);

  if (dist <= MELEE_RANGE && input.canAttack && cooldown === 0) {
    const playerGrabbable =
      (input.targetIsGrounded ?? true) && !(input.targetIsInvulnerable ?? false);

    if (playerGrabbable) {
      return idle(facing, {
        node: 'grab_windup',
        cooldown,
        reposition: 0,
        windup: GRAB_WINDUP_FRAMES,
      });
    }
    // Player airborne or invulnerable — fall back to punch
    return idle(facing, {
      node: 'windup',
      cooldown,
      reposition: 0,
      windup: WINDUP_FRAMES,
    });
  }

  return approachOrbit(input, state, facing, cooldown);
}

// ─── Jumper variant ──────────────────────────────────────────────────────────

function tickJumperVariant(
  input: EnemyAIInput,
  state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  if (reposition > 0) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'reposition', cooldown, reposition, windup: 0 },
    };
  }

  // Attack windup after landing
  if (windup > 0) {
    return idle(facing, { node: state.node, cooldown, reposition: 0, windup });
  }

  // Fire attack when normal windup expires
  if (state.node === 'windup') {
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      nextState: {
        node: 'reposition',
        cooldown: cooldownDuration,
        reposition: REPOSITION_FRAMES,
        windup: 0,
      },
    };
  }

  const { dx, dy, dist } = toTarget(input);

  // Continuing jump_charge: fast approach toward target
  if (state.node === 'jump_charge') {
    if (dist <= MELEE_RANGE) {
      // Landed — enter windup if allowed
      if (input.canAttack && cooldown === 0) {
        return idle(facing, { node: 'windup', cooldown, reposition: 0, windup: WINDUP_FRAMES });
      }
      return idle(facing, { node: 'scan', cooldown, reposition: 0, windup: 0 });
    }
    const move = norm(dx, dy, dist);
    return {
      moveX: move.x * JUMP_SPEED_MUL,
      moveY: move.y * JUMP_SPEED_MUL,
      facing,
      wantsAttack: false,
      nextState: { node: 'jump_charge', cooldown, reposition: 0, windup: 0 },
    };
  }

  // In melee range → normal windup
  if (dist <= MELEE_RANGE && input.canAttack && cooldown === 0) {
    return idle(facing, { node: 'windup', cooldown, reposition: 0, windup: WINDUP_FRAMES });
  }

  // Jump range → trigger jump charge
  if (dist > MELEE_RANGE && dist <= JUMP_TRIGGER_RANGE && input.canAttack && cooldown === 0) {
    const move = norm(dx, dy, dist);
    return {
      moveX: move.x * JUMP_SPEED_MUL,
      moveY: move.y * JUMP_SPEED_MUL,
      facing,
      wantsAttack: false,
      wantsJump: true,
      nextState: { node: 'jump_charge', cooldown, reposition: 0, windup: 0 },
    };
  }

  return approachOrbit(input, state, facing, cooldown);
}

// ─── Bomber variant ──────────────────────────────────────────────────────────

function tickBomberVariant(
  input: EnemyAIInput,
  state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  if (reposition > 0) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'reposition', cooldown, reposition, windup: 0 },
    };
  }

  if (windup > 0) {
    return idle(facing, { node: 'windup', cooldown, reposition: 0, windup });
  }

  // Bomb fires when windup expires
  if (state.node === 'windup') {
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      wantsBomb: true,
      nextState: {
        node: 'reposition',
        cooldown: cooldownDuration,
        reposition: REPOSITION_FRAMES,
        windup: 0,
      },
    };
  }

  const { dx, dy, dist } = toTarget(input);

  // In melee range → enter windup (bomb triggers on expiry)
  if (dist <= MELEE_RANGE && input.canAttack && cooldown === 0) {
    return idle(facing, { node: 'windup', cooldown, reposition: 0, windup: WINDUP_FRAMES });
  }

  // Kamikaze rush — ignore orbit, go straight at target
  const move = norm(dx, dy, dist);
  return {
    moveX: move.x * BOMB_SPEED_MUL,
    moveY: move.y * BOMB_SPEED_MUL,
    facing,
    wantsAttack: false,
    nextState: { node: 'bomb_charge', cooldown, reposition: 0, windup: 0 },
  };
}

// ─── Sniper variant ──────────────────────────────────────────────────────────

function tickSniperVariant(
  input: EnemyAIInput,
  _state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
  reposition: number,
  _windup: number,
  cooldownDuration: number,
): EnemyAIOutput {
  if (reposition > 0) {
    const { dx, dy, dist } = toTarget(input);
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'reposition', cooldown, reposition, windup: 0 },
    };
  }

  const { dx, dy, dist } = toTarget(input);

  // Too close → retreat
  if (dist < SNIPER_HOLD_RANGE) {
    const away = norm(-dx, -dy, dist);
    return {
      moveX: away.x,
      moveY: away.y,
      facing,
      wantsAttack: false,
      nextState: { node: 'scan', cooldown, reposition: 0, windup: 0 },
    };
  }

  // In preferred range → fire if ready, otherwise hold
  if (dist <= SNIPER_RANGE) {
    if (input.canAttack && cooldown === 0) {
      return {
        moveX: 0,
        moveY: 0,
        facing,
        wantsAttack: false,
        wantsRanged: true,
        nextState: {
          node: 'reposition',
          cooldown: cooldownDuration,
          reposition: REPOSITION_FRAMES,
          windup: 0,
        },
      };
    }
    return idle(facing, { node: 'sniper_hold', cooldown, reposition: 0, windup: 0 });
  }

  // Too far → approach slowly
  const move = norm(dx, dy, dist);
  return {
    moveX: move.x,
    moveY: move.y,
    facing,
    wantsAttack: false,
    nextState: { node: 'scan', cooldown, reposition: 0, windup: 0 },
  };
}

// ─── Biker variant ───────────────────────────────────────────────────────────

function toBikerPhase(node: BehaviourNode): BikerPhase {
  if (
    node === 'biker_approach' ||
    node === 'biker_strafe' ||
    node === 'biker_recover' ||
    node === 'biker_downed'
  ) {
    return node;
  }
  return 'biker_approach';
}

function tickBikerVariant(input: EnemyAIInput, state: EnemyAIState, facing: 1 | -1): EnemyAIOutput {
  const bikerState: BikerAIState = {
    phase: toBikerPhase(state.node),
    strafeDir: state.bikerStrafeDir ?? 1,
    mountedHits: state.bikerMountedHits ?? 0,
    isKnockedOff: state.bikerKnockedOff ?? false,
    recoverFrames: state.bikerRecoverFrames ?? 0,
  };

  const out = tickBikerAI(
    {
      selfX: input.selfX,
      selfY: input.selfY,
      targetY: input.targetY,
      hitRegisteredThisFrame: input.bikerHitRegistered ?? false,
      worldWidth: input.worldWidth ?? 960,
      knockOffHits: input.bikerKnockOffHits,
    },
    bikerState,
  );

  return {
    moveX: out.moveX,
    moveY: out.moveY,
    facing,
    wantsAttack: false,
    wantsStrafeContact: out.wantsStrafeContact,
    wantsKnockOff: out.wantsKnockOff,
    isKnockedOff: out.isKnockedOff,
    nextState: {
      node: out.nextState.phase,
      cooldown: state.cooldown,
      reposition: 0,
      windup: 0,
      bikerStrafeDir: out.nextState.strafeDir,
      bikerMountedHits: out.nextState.mountedHits,
      bikerKnockedOff: out.nextState.isKnockedOff,
      bikerRecoverFrames: out.nextState.recoverFrames,
    },
  };
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

function approachOrbit(
  input: EnemyAIInput,
  _state: EnemyAIState,
  facing: 1 | -1,
  cooldown: number,
): EnemyAIOutput {
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);
  const { dist } = toTarget(input);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: BehaviourNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      nextState: { node, cooldown, reposition: 0, windup: 0 },
    };
  }

  return idle(facing, { node: 'scan', cooldown, reposition: 0, windup: 0 });
}

function cooldownFor(aggressionScale: number): number {
  return Math.round(BASE_COOLDOWN * (1 - aggressionScale * 0.5));
}

function toTarget(input: EnemyAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function idle(facing: 1 | -1, nextState: EnemyAIState): EnemyAIOutput {
  return { moveX: 0, moveY: 0, facing, wantsAttack: false, nextState };
}
